/**
 * Primitives data model — the flat-Map decomposition.
 *
 * A landing page is four sibling Maps + a `rootId` + a `version`. Children
 * are referenced by id, never nested. Props and styles live in their own
 * Maps so a single property change is one Map entry update, not a deep
 * tree mutation.
 *
 * This is Webstudio's pattern (apps/builder/app/shared/sync/data-stores.ts)
 * adapted to OuterJoyn's narrower needs: 4 Maps instead of 11. See
 * docs-content/plans/LANDING-PAGE-PRIMITIVES-PLAN.md for the architectural
 * rationale and docs-content/research/OSS-PAGE-EDITOR-PATTERNS.md for the
 * source-code citations the design is based on.
 *
 * v1 invariants:
 * - `rootId` always references an instance in `instances`.
 * - Every `Instance.children[i]` references an instance in `instances`.
 * - Every prop entry's `instanceId` references an instance in `instances`.
 * - Every style entry's `sourceId` references a source in `styleSources`.
 * - Style sources of type `local` belong to exactly one instance and live
 *   only as long as the instance does.
 * - Style sources of type `token` are shared across instances and outlive
 *   any single one. They are managed by brand setup, not by page edits.
 * - `version` is monotonic and bumps once per persisted patch batch.
 *
 * The `version` integer is the rebase-on-mismatch primitive: the server
 * rejects writes whose `version` is not exactly `current + 1`. Stale
 * clients refetch and replay their pending patches against the new base.
 */

// ─────────────────────────────────────────────────────────────────────────
// Identifiers
// ─────────────────────────────────────────────────────────────────────────

/** Stable id for an Instance. Server-generated, never reused. */
export type NodeId = string;

/** Stable id for a StyleSource (local-per-instance OR named-token). */
export type SourceId = string;

/** Stable id for a Breakpoint. Defaults defined in `defaultBreakpoints`. */
export type BreakpointId = string;

// ─────────────────────────────────────────────────────────────────────────
// Instance type vocabulary
// ─────────────────────────────────────────────────────────────────────────

/** Layout primitives — generic containers. Don't render visible content
 *  on their own; hold other instances. */
export type LayoutType =
  | 'section'
  | 'stack'
  | 'row'
  | 'grid'
  | 'container';

/** Content leaves — atoms with style/content props. Children disallowed
 *  (enforced by the content model). */
export type ContentType =
  | 'heading'
  | 'text'
  | 'image'
  | 'button'
  | 'video'
  | 'logo'
  | 'spacer'
  | 'divider'
  /** Escape hatch: arbitrary HTML the customer pastes when no
   *  primitive fits. DOMPurify-sanitized at write time, rendered
   *  inside a sandboxed iframe so even sanitizer-bypass attempts
   *  can't reach the parent document. Last resort — the LLM is
   *  instructed in the system prompt to prefer named primitives. */
  | 'custom-html';

/** Universal HTML escape hatch — Webstudio's `ws:element` pattern. The
 *  `tag` prop names the underlying HTML element (`aside`, `figure`,
 *  `figcaption`, `nav`, etc.). Use sparingly; prefer named primitives. */
export type ElementType = 'element';

/** Registered SDK components — wrap existing OuterJoyn React components
 *  via custom-element tags (Webstudio + Plasmic pattern). The closed
 *  vocabulary is the anti-phishing defense: anything not in this list is
 *  rejected by the LLM tool validator and the published-page renderer. */
export type RegisteredComponentType =
  | 'oj-link-account-button'
  | 'oj-pay'
  | 'oj-redeem'
  | 'oj-rewards-grid'
  | 'oj-account'
  | 'oj-exchange'
  /** Form primitives. `oj-form-input` captures a single field;
   *  `oj-form-submit` collects all sibling form-inputs under the
   *  closest ancestor section/stack and POSTs to
   *  /api/landing-pages/form-submissions. Replaces the legacy
   *  `newsletter`, `contact-form`, and `account-link-form` blocks. */
  | 'oj-form-input'
  | 'oj-form-submit';

/** Discriminated `Instance.type`. The compiler maps each to a renderer. */
export type InstanceType =
  | LayoutType
  | ContentType
  | ElementType
  | RegisteredComponentType;

/** Instances that may have children. Enforced at the type level so callers
 *  cannot set `children` on a content leaf. */
export const LAYOUT_TYPES: ReadonlyArray<LayoutType> = [
  'section',
  'stack',
  'row',
  'grid',
  'container',
];

/** Set lookup for layout types — used by the content model and tools. */
export const LAYOUT_TYPE_SET: ReadonlySet<InstanceType> = new Set<InstanceType>(LAYOUT_TYPES);

/** Content leaves — atoms. Cannot have children. */
export const CONTENT_TYPES: ReadonlyArray<ContentType> = [
  'heading',
  'text',
  'image',
  'button',
  'video',
  'logo',
  'spacer',
  'divider',
  'custom-html',
];

export const CONTENT_TYPE_SET: ReadonlySet<InstanceType> = new Set<InstanceType>(CONTENT_TYPES);

/** Registered components. Cannot have children unless the component
 *  declares slots (post-v1 feature). */
export const REGISTERED_COMPONENT_TYPES: ReadonlyArray<RegisteredComponentType> = [
  'oj-link-account-button',
  'oj-pay',
  'oj-redeem',
  'oj-rewards-grid',
  'oj-account',
  'oj-exchange',
  'oj-form-input',
  'oj-form-submit',
];

export const REGISTERED_COMPONENT_TYPE_SET: ReadonlySet<InstanceType> =
  new Set<InstanceType>(REGISTERED_COMPONENT_TYPES);

// ─────────────────────────────────────────────────────────────────────────
// Instance — the topology node
// ─────────────────────────────────────────────────────────────────────────

/**
 * An instance is a single node in the page tree. It carries only id,
 * type, and child references — props and styles live in sibling Maps.
 *
 * The deliberate omission of `props` from this type is the entire reason
 * the model is fast: a "change one prop" edit becomes a single entry
 * update in the `props` Map and never touches the `instances` Map.
 */
export interface Instance {
  id: NodeId;
  type: InstanceType;

  /** Ordered list of child ids. Empty for content leaves. For
   *  registered components with slots (post-v1) this becomes
   *  `Record<slotName, NodeId[]>`. */
  children: NodeId[];

  /** Optional human-readable label shown in the layers panel. The LLM is
   *  encouraged to set this to a short summary (e.g. "Hero section",
   *  "Glow logo") so the layers panel and edit transcripts are
   *  intelligible. Auto-generated if absent. */
  label?: string;
}

// ─────────────────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────────────────

/**
 * Per-instance prop values. Schema is per-instance-type and validated by
 * `primitives/schema.ts` (built next phase). The shape is intentionally
 * loose at this layer — runtime validation happens at the schema/tool
 * boundary, not in the storage model.
 *
 * Convention for behavior wiring: `button` instances have an optional
 * `onClick` prop whose value is one of the registered actions from the
 * action catalog (`actions/registry.ts`):
 *
 *   { type: 'navigate'; url: string }
 *   { type: 'linkAccount'; partnershipId; oauthProfileId; scopeSubset? }
 *   ... future actions
 *
 * Anything not in the registry is rejected at compile time.
 */
export type NodeProps = Record<string, unknown>;

// ─────────────────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────────────────

/** CSS pseudo-states the style system supports. Empty string for the
 *  base / no-state declaration. */
export type PseudoState =
  | ''
  | ':hover'
  | ':focus'
  | ':active'
  | ':disabled';

/**
 * A single style declaration. Keyed by
 * `(sourceId, breakpointId, property, state)` — the composite key acts
 * as the primary key in the Map and is the single most useful piece of
 * the Webstudio architecture to copy.
 *
 * The `value` discriminator is what powers soft token enforcement: tokens
 * resolve to CSS variables at compile time; literals emit raw values
 * with `data-oj-literal="true"` on the parent instance for audit. The LLM
 * is told to prefer tokens but is not forced.
 */
export interface StyleDecl {
  sourceId: SourceId;
  breakpointId: BreakpointId;
  /** CSS property in camelCase or kebab-case. The compiler normalizes. */
  property: string;
  state: PseudoState;
  value: StyleValue;
}

/** Composite key matching the `StyleDecl` primary key. Used as the Map
 *  key so lookups by `(source, bp, prop, state)` are O(1). */
export type StyleKey = `${SourceId}|${BreakpointId}|${string}|${PseudoState}`;

/** Build the composite key from its parts. */
export function styleKey(
  sourceId: SourceId,
  breakpointId: BreakpointId,
  property: string,
  state: PseudoState,
): StyleKey {
  return `${sourceId}|${breakpointId}|${property}|${state}`;
}

/** Parse a composite key back into its parts. Used by undo/redo when
 *  reverting style operations whose original key isn't passed through. */
export function parseStyleKey(key: StyleKey): {
  sourceId: SourceId;
  breakpointId: BreakpointId;
  property: string;
  state: PseudoState;
} {
  const [sourceId, breakpointId, property, state] = key.split('|') as [
    SourceId,
    BreakpointId,
    string,
    PseudoState,
  ];
  return { sourceId, breakpointId, property, state };
}

/**
 * Style values are either token references (resolved at compile time
 * to `var(--oj-*)`) or literals (emitted as-is, marked
 * `data-oj-literal="true"` on the consuming instance).
 */
export type StyleValue =
  | { type: 'token'; tokenId: string }
  | { type: 'literal'; value: string };

// ─────────────────────────────────────────────────────────────────────────
// Style sources
// ─────────────────────────────────────────────────────────────────────────

/**
 * Style sources are the things that own style declarations. Two flavors:
 *
 * - `local`: bound to one instance. Owns one-off style overrides. Created
 *   automatically when a customer makes an instance-specific tweak; reaped
 *   when the instance is deleted.
 *
 * - `token`: shared across instances. Named (e.g. `oj-color-primary`,
 *   `oj-font-heading`). Values are usually managed by brand setup, not
 *   by page edits.
 *
 * The two share the same `styles` Map keyed by source — a `StyleDecl`
 * never knows or cares whether its source is local or token. This
 * uniformity is what makes "convert literal to token" a pure
 * source-rebinding operation rather than a value migration.
 */
export type StyleSource = StyleSourceLocal | StyleSourceToken;

export interface StyleSourceLocal {
  id: SourceId;
  type: 'local';
  /** The instance this local source is bound to. */
  instanceId: NodeId;
}

export interface StyleSourceToken {
  id: SourceId;
  type: 'token';
  /** Token name. Convention: `oj-color-*`, `oj-font-*`, `oj-space-*`,
   *  `oj-radius-*`, `oj-shadow-*`. The compiler emits
   *  `var(--<name>)`. */
  name: string;
}

// ─────────────────────────────────────────────────────────────────────────
// Style source selections
// ─────────────────────────────────────────────────────────────────────────

/**
 * Per-instance ordered list of style sources that apply to it. Multiple
 * sources can apply (a token + a local override); later entries win on
 * conflict per CSS cascade (specificity equal, later declared wins).
 *
 * Stored as a separate Map keyed by `instanceId` so queries like "which
 * sources apply to this instance" are O(1). Webstudio's
 * `StyleSourceSelections` Map serves the same role.
 */
export interface StyleSourceSelection {
  instanceId: NodeId;
  /** Ordered list of source ids, lowest priority first. */
  values: SourceId[];
}

// ─────────────────────────────────────────────────────────────────────────
// Breakpoints
// ─────────────────────────────────────────────────────────────────────────

/**
 * Breakpoint definitions for responsive overrides. Style declarations are
 * keyed by breakpoint, so "make the heading 64px on desktop, 40px on
 * mobile" is two `StyleDecl` entries with different `breakpointId`.
 *
 * v1 ships three breakpoints; the system supports arbitrary additions.
 * `minWidth: 0` is the base / always-applies breakpoint and must always
 * be present; the others are progressive enhancements.
 */
export interface Breakpoint {
  id: BreakpointId;
  label: string;
  /** Min width in px. The base breakpoint has `minWidth: 0`. */
  minWidth: number;
}

/** Default breakpoint set. Mobile-first: base = phone-sized, escalating
 *  up. Customers can extend or replace this in brand setup. */
export const DEFAULT_BREAKPOINTS: ReadonlyArray<Breakpoint> = [
  { id: 'bp-base', label: 'Mobile', minWidth: 0 },
  { id: 'bp-tablet', label: 'Tablet', minWidth: 768 },
  { id: 'bp-desktop', label: 'Desktop', minWidth: 1280 },
];

// ─────────────────────────────────────────────────────────────────────────
// LandingPageData — the whole document
// ─────────────────────────────────────────────────────────────────────────

/**
 * The root document. Four sibling Maps + `rootId` + `version`. This is
 * the unit that lives in `LandingPage.contentJson` for `format:
 * 'tree-v1'` pages. The legacy `pageBlocks` shape lives in the same
 * column for `format: 'blocks-v1'` pages — the discriminator on the
 * page record routes the renderer.
 *
 * Maps are *plain* `Map<K, V>` objects. They serialize to/from JSON via
 * the `serializeLandingPageData` / `parseLandingPageData` helpers in
 * `primitives/serialization.ts` (built next).
 */
export interface LandingPageData {
  /** All instances in the page, keyed by id. Tree topology only — props
   *  and styles live elsewhere. */
  instances: Map<NodeId, Instance>;

  /** Per-instance prop values. Keyed by `instanceId`. */
  props: Map<NodeId, NodeProps>;

  /** All style declarations, keyed by composite `StyleKey`. */
  styles: Map<StyleKey, StyleDecl>;

  /** Style source registry (local + token sources). */
  styleSources: Map<SourceId, StyleSource>;

  /** Per-instance ordered list of source ids. */
  styleSourceSelections: Map<NodeId, StyleSourceSelection>;

  /** Breakpoint definitions for the page. v1 uses defaults. */
  breakpoints: Map<BreakpointId, Breakpoint>;

  /** The root of the tree. Always references an entry in `instances`. */
  rootId: NodeId;

  /**
   * Monotonic version. Bumps once per persisted patch batch. The server
   * rejects writes whose `version` is not exactly `current + 1`. Stale
   * clients refetch and replay their pending patches against the new
   * base — this is Webstudio's `Build.version` rebase pattern.
   */
  version: number;
}

// ─────────────────────────────────────────────────────────────────────────
// Edit-source labels
// ─────────────────────────────────────────────────────────────────────────

/**
 * Source of a mutation. Used by the History manager to decide whether a
 * given edit goes on the undo stack.
 *
 * - `user`: direct manipulation (click + edit, drag, keyboard). Goes on
 *   the undo stack. The customer expects `Cmd-Z` to revert these.
 *
 * - `llm`: AI tool-call. Goes on the undo stack. The customer expects
 *   `Cmd-Z` to revert the entire AI turn as a single entry, not patch
 *   by patch.
 *
 * - `remote`: incoming edit from another collaborator (multiplayer,
 *   post-v1). Applies silently. *Never* on the undo stack — undoing
 *   someone else's edit is a multiplayer footgun.
 *
 * - `migration`: legacy-block-to-tree migration. Applies silently and
 *   creates a separate version snapshot the customer can roll back to,
 *   so this never pollutes the in-session undo stack.
 *
 * tldraw's `addHistoryInterceptor((entry, source) => …)` uses the same
 * shape (`'user' | 'remote' | 'system'`); we add `llm` and `migration`
 * as OuterJoyn-specific sources.
 */
export type EditSource = 'user' | 'llm' | 'remote' | 'migration';
